import { useCallback, useEffect, useState } from 'react'
import { fetchMarkdownFromGCS } from '@/util/fetch-markdown-from-gcs'

type UseArtifactMarkdownLoaderProps = {
  gcsBucketName: string
  filePath: string
}

type UseArtifactMarkdownLoaderResult = {
  markdownString: string | null
  isLoading: boolean
  hasError: boolean
  retry: () => void
}

/**
 * Load an artifact's markdown content from GCS for the editor.
 * Returns the raw markdown string — ProseMirror in MarkdownEditor consumes it directly,
 * so no HTML conversion or highlight processing happens here.
 */
export function useArtifactMarkdownLoader(props: UseArtifactMarkdownLoaderProps): UseArtifactMarkdownLoaderResult {
  const { gcsBucketName, filePath } = props
  const [markdownString, setMarkdownString] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)

  const fetchMarkdown = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)
    setMarkdownString(null)

    try {
      const markdown = await fetchMarkdownFromGCS(gcsBucketName, filePath)
      setMarkdownString(markdown)
    } catch (err) {
      console.error('Error loading artifact markdown:', err)
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }, [gcsBucketName, filePath])

  useEffect(() => {
    fetchMarkdown()
  }, [fetchMarkdown])

  return { markdownString, isLoading, hasError, retry: fetchMarkdown }
}
